import { useEffect, useRef } from "react";
import { useAuthSession } from "../../../lib/auth/session";
import { useEffectiveOfflineMode } from "../../../lib/offline/status";
import { OFFLINE_CONFIG_RESOURCES, resolveResourceTtl, type OfflineConfigResource } from "./catalog";
import { useOfflineCacheDispatch } from "../state/OfflineCacheStatusContext";
import { useOfflineCachePolicy } from "../state/OfflineCachePolicyProvider";
import type { OfflineCachePolicy } from "../../../lib/offline/cachePolicy";
import { fetchResource, shouldFetchResource } from "./resourceUtils";

const resolveTtlForResource = (resource: OfflineConfigResource, policy: OfflineCachePolicy) =>
  resolveResourceTtl(resource, policy);

export const useOfflineConfigPrefetch = () => {
  const { status, network, offlineEnabled } = useEffectiveOfflineMode();
  const session = useAuthSession();
  const inFlightRef = useRef(false);
  const dispatch = useOfflineCacheDispatch();
  const policy = useOfflineCachePolicy();

  useEffect(() => {
    if (!offlineEnabled) return;
    if (network !== "online") return;
    if (status !== "online") return;
    if (inFlightRef.current) return;

    let cancelled = false;
    inFlightRef.current = true;
    (async () => {
      const resourcesToFetch: Array<{ resource: OfflineConfigResource; ttlMs: number | null }> = [];
      for (const resource of OFFLINE_CONFIG_RESOURCES) {
        if (cancelled) break;
        const ttlMs = resolveTtlForResource(resource, policy);
        const needsFetch = await shouldFetchResource(resource, ttlMs);
        if (needsFetch) {
          resourcesToFetch.push({ resource, ttlMs });
        }
      }
      if (cancelled) {
        return;
      }
      if (resourcesToFetch.length === 0) {
        dispatch.reset();
        return;
      }
      dispatch.start(resourcesToFetch.length);
      for (let index = 0; index < resourcesToFetch.length; index += 1) {
        if (cancelled) break;
        const { resource, ttlMs } = resourcesToFetch[index];
        dispatch.progress(index, resource.key);
        await fetchResource(resource, ttlMs);
        dispatch.progress(index + 1, null);
      }
      if (!cancelled) {
        dispatch.complete();
      }
    })()
      .catch((error) => {
        console.warn("Offline config prefetch failed", error);
        dispatch.error(error instanceof Error ? error.message : undefined);
      })
      .finally(() => {
        inFlightRef.current = false;
      });

    return () => {
      cancelled = true;
      if (inFlightRef.current) {
        dispatch.reset();
      }
    };
  }, [dispatch, offlineEnabled, network, status, session.officerId, policy]);
};
